import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import KnowledgeGraphPage from 'src/sections/knowledge-graph/knowledge-graph';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
const UserProfilePage = lazy(() => import('src/pages/main-dashboard/users/profile'));
const UserListPage = lazy(() => import('src/pages/main-dashboard/users/list')); 
const TacPage = lazy(() => import('src/pages/main-dashboard/terms-and-conditions/tac-page'));
const FaqPage = lazy(() => import('src/pages/main-dashboard/faq/faq-page'));
const AboutUsPage = lazy(() => import('src/pages/main-dashboard/about-us/about-us-page'));
const PrivacyPolicyPage = lazy(() => import('src/pages/main-dashboard/privacy-policy/privacy-policy-page'));
const SupportPage = lazy(() => import('src/pages/main-dashboard/support/support-page'));
const SupportProfilePage = lazy(() => import('src/pages/main-dashboard/support/profile'));
const RoomListPage = lazy(() => import('src/pages/main-dashboard/rooms/list'));
const RoomProfilePage = lazy(() => import('src/pages/main-dashboard/rooms/profile')); 
const RoomParticipantListPage = lazy(() => import('src/pages/main-dashboard/room-participants/list'));
const RoomParticipantProfilePage = lazy(() => import('src/pages/main-dashboard/room-participants/profile'));
const TagListPage = lazy(() => import('src/pages/main-dashboard/tags/list'));
const TagProfilePage = lazy(() => import('src/pages/main-dashboard/tags/profile'));
const NotificationListPage = lazy(() => import('src/pages/main-dashboard/notifications/list'));
const NotificationProfilePage = lazy(() => import('src/pages/main-dashboard/notifications/profile'));
const SettingsListPage = lazy(() => import('src/pages/main-dashboard/settings/list'));
const SettingsProfilePage = lazy(() => import('src/pages/main-dashboard/settings/profile'));
const AccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const AnnoucementPage = lazy(() => import('src/pages/main-dashboard/annoucements/annoucement'));

const SubscriptionPlanPage = lazy(() => import('src/pages/main-dashboard/subscription-plans/ai-subscription-plan'));
const PaymentPage = lazy(() => import('src/pages/main-dashboard/payments/list'));



// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ai-usage', element: <OverviewEcommercePage />},
      { path: 'faq', element: <FaqPage /> },
      { path: 'about-us', element: <AboutUsPage /> },
      { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
      { path: 'support', element: <SupportPage /> },
      { path: 'support/profile/:id', element: <SupportProfilePage /> },
      { path: 'terms-and-conditions', element: <TacPage />},
      { path: 'annoucement', element: <AnnoucementPage />},
      // OTHER PAGES
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      { path: 'knowledge-graph', element: <KnowledgeGraphPage />},
      {
        path: 'user',
        children: [
          { element: <UserListPage />, index: true },
          { path: 'list', element: <UserListPage /> },
          { path: 'profile/:id', element: <UserProfilePage /> },
          { path: 'account', element: <AccountPage /> },
        ],
      },
      {
        path: 'room',
        children: [
          { element: <RoomListPage />, index: true },
          { path: 'list', element: <RoomListPage /> },
          { path: 'profile/:id', element: <RoomProfilePage /> },
        ],
      },
      {
        path: 'room-participants',
        children: [
          { element: <RoomParticipantListPage />, index: true },
          { path: 'list', element: <RoomParticipantListPage /> },
          { path: 'profile/:id', element: <RoomParticipantProfilePage /> },
        ],
      },
      {
        path: 'tags',
        children: [
          { element: <TagListPage />, index: true },
          { path: 'list', element: <TagListPage /> },
          { path: 'profile/:id', element: <TagProfilePage /> },
        ],
      },
      {
        path: 'ai-subscription-plans',  
        children: [
          { element: <SubscriptionPlanPage />, index: true },
          { path: 'list', element: <SubscriptionPlanPage /> },
        ],
      },
      {
        path: 'payments',  
        children: [
          { element: <PaymentPage />, index: true },
          { path: 'list', element: <PaymentPage /> },
        ],
      },
      {
        path: 'notifications',
        children: [
          { element: <NotificationListPage />, index: true },
          { path: 'list', element: <NotificationListPage /> },
          { path: 'profile/:id', element: <NotificationProfilePage /> },
        ],
      },
      {
        path: 'settings',
        children: [
          { element: <SettingsListPage />, index: true },
          { path: 'list', element: <SettingsListPage /> },
          { path: 'profile/:id', element: <SettingsProfilePage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
];
